// packages
import { h } from 'preact';
import { widont, intcomma, intword } from 'journalize';
import debounce from '../utils/debounce';
import PureComponent from './PureComponent';
import * as d3 from 'd3';
import { wordwrap } from 'd3-jetpack';

class BarChart extends PureComponent {
  constructor() {
    super();

    this.getContainerWidth = this.getContainerWidth.bind(this);
    this.createChart = this.createChart.bind(this);
    this.clearContainers = this.clearContainers.bind(this);

    this.state = {
      breakPoint: 664,
    };
  }

  getContainerWidth(base) {
    const body = d3.select(base);
    const width = body.node().offsetWidth;

    return width;
    // this.setState({
    //   width,
    // });
  }

  clearContainers(year) {
    const body = d3.select(`.bar-chart-container-${year}`);
    body.each(function(d) {
      d3.select(this).html('');
    });
    // base.html('');
  }

  componentDidMount() {
    // set up listeners
    window.addEventListener('resize', this.createChart);

    // initial run
    this.createChart();
  }

  componentDidUpdate(prevProps) {
    if (this.props.county != prevProps.county) {
      this.createChart();
    }
  }

  createChart() {
    const { yearData, statewideMax, toggle, base } = this.props;
    const { breakPoint } = this.state;

    let frameWidth = this.getContainerWidth(base),
      bodyWidth = this.getContainerWidth('body');
    this.clearContainers(yearData.year);

    // for stacked bars
    let stackedData = d3
      .stack()
      .keys(['republican', 'democratic', 'didnotvote'])([yearData]);

    let groupedData = [
      {
        category: 'republican',
        value: yearData.republican,
      },
      {
        category: 'democratic',
        value: yearData.democratic,
      },
      {
        category: 'didnotvote',
        value: yearData.didnotvote,
      },
    ];

    let margin = {
      top: 25,
      left: 50,
      right: 10,
      bottom: 50,
    };

    let height = 400;
    if (bodyWidth < breakPoint) height = 300;
    if (bodyWidth > breakPoint && yearData.year != '2018') margin.left = 10;

    // append svg
    let svg = d3
        .select(`.bar-chart-container-${yearData.year}`)
        .append('svg')
        .attr('width', frameWidth)
        .attr('height', height),
      g = svg
        .append('g')
        .attr('class', 'stacked-bar-chart')
        .attr('width', chartWidth)
        .attr('transform', `translate(${margin.left},${margin.top})`),
      labels_g = g.append('g').attr('class', 'labels-container');

    let xAxis = g.append('g').attr('class', 'axis axis--x'),
      yAxis = g.append('g').attr('class', 'axis axis--y');

    let chartWidth = frameWidth - margin.left - margin.right,
      chartHeight = height - margin.top - margin.bottom;

    // set ranges
    let x = d3
      .scaleBand()
      .domain(['republican', 'democratic', 'didnotvote']) // [yearData.year] for stacked bar
      .padding(0.25);
    let y = d3
      .scaleLinear()
      .domain([0, statewideMax])
      .nice();

    x.range([0, chartWidth]);
    y.range([chartHeight, 0]);

    xAxis
      .call(
        d3
          .axisBottom(x)
          .tickSize(0)
          .tickFormat('')
      )
      .attr('transform', `translate(0,${chartHeight})`);

    yAxis.call(
      d3
        .axisLeft(y)
        .ticks(5)
        .tickSize(-chartWidth)
        .tickFormat(d => {
          if (bodyWidth > breakPoint) {
            if (yearData.year != '2018') return '';
            else return intcomma(intword(d)).replace(' million', 'M');
          } else {
            return intcomma(intword(d)).replace(' million', 'M');
          }
        })
      // .tickSize(0)
      // .tickFormat('')
      // .ticks(3)
      // .tickSize(-chartWidth)
      // .tickFormat(d => `${d * 100}%`)
    );

    // grouped bars
    let barGroups = g
      .selectAll('rect')
      .data(groupedData)
      .enter()
      .append('rect')
      .attr('x', function(d) {
        return x(d.category);
      })
      .attr('y', function(d) {
        return y(d.value);
      })
      .attr('width', x.bandwidth())
      .attr('height', function(d) {
        return chartHeight - y(d.value);
      })
      .attr('fill', d => {
        if (d.category == 'republican') return '#f2593b';
        if (d.category == 'democratic') return '#1755c5';
        if (d.category == 'didnotvote') return '#99a0af';
      })
      .attr('display', () => {
        if (toggle.is_there_sos_data == 'NO' && yearData.year == '2022')
          return 'none';
        else return 'initial';
      });

    if (toggle.is_there_sos_data == 'NO' && yearData.year == '2022') {
      let noData = g.append('g');

      let noDataHeight = 50,
        noDataWidth = chartWidth - 20;
      noData
        .append('rect')
        .attr('height', noDataHeight)
        .attr('width', noDataWidth)
        .attr('x', chartWidth / 2 - noDataWidth / 2)
        .attr('y', chartHeight / 2 - noDataHeight / 2)
        .attr('fill', '#f7f7f7')
        .attr('opacity', 0.8);

      noData
        .append('text')
        .attr('y', chartHeight / 2 - 3)
        .text('')
        .tspans(wordwrap('No turnout data is available yet', 20))
        .attr('x', chartWidth / 2)
        .attr('text-anchor', 'middle')
        .attr('class', 'no-data-text');
    }

    let partyLabels = labels_g
      .selectAll('g')
      .data(['republican', 'democratic'])
      .enter()
      .append('g')
      .attr(
        'transform',
        d => `translate(${x(d) + x.bandwidth()},${y(0) + 20})`
      );

    partyLabels
      .append('circle')
      .attr('r', 10)
      .attr('cy', -4.5)
      .attr('cx', -x.bandwidth() / 2)
      .attr('class', d => {
        if (toggle.is_there_sos_data == 'NO' && yearData.year == '2022')
          return 'gray-circle';
        else return `${d}-circle`;
      });

    partyLabels
      .append('text')
      .text(d => d[0])
      .attr('dy', 0)
      .attr('dx', -x.bandwidth() / 2 - 4)
      .attr('class', 'circle-text');

    labels_g
      .append('text')
      .attr('y', y(0) + 20)
      .text('')
      .tspans(
        wordwrap(
          yearData.year == '2022' && toggle.is_election_over == 'NO'
            ? 'Did not vote yet'
            : 'Did not vote',
          10
        )
      )
      .attr('x', x('didnotvote') + x.bandwidth() / 2)
      .attr('text-anchor', 'middle')
      .attr('class', 'didnotvote-text');

    if (toggle.is_there_sos_data != 'NO' || yearData.year != '2022') {
      labels_g
        .selectAll('.percent-text')
        .data(groupedData)
        .enter()
        .append('g')
        .attr('class', 'percent-text')
        .attr('transform', d => `translate(0,${y(d.value) - 10})`)
        .append('text')
        .text(d => {
          let percentTurnout = (d.value / yearData.registered_voters) * 100,
            decimalPlaces;
          if (percentTurnout < 1) decimalPlaces = 1;
          else decimalPlaces = 1;

          return `${parseFloat(percentTurnout.toFixed(decimalPlaces))}%`;
        })
        .attr('x', function(d) {
          return (
            x(d.category) +
            x.bandwidth() / 2 -
            d3
              .select(this)
              .node()
              .getBoundingClientRect().width /
              2
          );
        });
    }

    // stacked bars
    // let barGroups = g
    //   .append('g')
    //   .attr('class', 'bar-groups-container')
    //   .selectAll('g')
    //   .data(groupedData) // stackedData for stacked bars
    //   .enter()
    //   .append('g')
    //   .attr('fill', d => {
    //     if (d.key == 'republican') return '#f2593b';
    //     if (d.key == 'democratic') return '#1755c5';
    //     if (d.key == 'didnotvote') return '#99a0af';
    //   })
    //   .attr('class', 'bar-groups');

    // let barRects = barGroups
    //   .selectAll('rect')
    //   .data(function(d) {
    //     return d;
    //   })
    //   .enter()
    //   .append('rect');

    // let barWidth = 25;
    // barRects
    //   .attr('x', function(d) {
    //     return x(d.data.year) - (barWidth - x.bandwidth()) / 2;
    //   })
    //   .attr('y', function(d) {
    //     return y(d[1]);
    //   })
    //   .attr('height', function(d) {
    //     let diff = y(d[0]) - y(d[1]);
    //     if (diff < 0) return 0;
    //     else return diff;
    //   })
    //   .attr('width', barWidth);

    // registered voters label for stacked bars
    // let registeredVotersLabel = labels_g
    //   .append('g')
    //   .datum(yearData)
    //   .attr('text-anchor', 'end')
    //   .attr('transform', d => {
    //     return `translate(${x(d.year) -
    //       (barWidth - x.bandwidth()) / 2 -
    //       85}, ${y(d['registered_voters']) + 5})`;
    //   })
    //   .attr('class', 'registered-voter-text label');

    // registeredVotersLabel
    //   .append('text')
    //   .text(d => intcomma(intword(d['registered_voters'])))
    //   .style('font-weight', 'bold');

    // registeredVotersLabel
    //   .append('text')
    //   .text('registered')
    //   .attr('dy', 15);

    // registeredVotersLabel
    //   .append('text')
    //   .text('voters')
    //   .attr('dy', 30);

    // party labels for stacked bars
    // if (yearData.year == 2022) {
    // let text = 'Data is delayed';
    // let noDataGroup = labels_g
    //   .append('g')
    //   .datum(yearData)
    //   .attr(
    //     'transform',
    //     d =>
    //       `translate(${x(d.year) - (barWidth - x.bandwidth()) / 2 - 40}, ${y(
    //         0
    //       ) - 20})`
    //   );
    // noDataGroup
    //   .append('text')
    //   .text('')
    //   .tspans(wordwrap(text, 12))
    //   .attr('class', 'label');
    // } else {
    //   let partyLabels = labels_g
    //     .append('g')
    //     .selectAll('g')
    //     .data(stackedData)
    //     .enter()
    //     .append('g')
    //     .attr('text-anchor', 'start')
    //     .style('display', d => (d.key == 'didnotvote' ? 'none' : 'initial'));

    //   let partyLabelsGroups = partyLabels
    //     .selectAll('g')
    //     .data(function(d) {
    //       d[0].key = d.key;
    //       return d;
    //     })
    //     .enter()
    //     .append('g')
    //     .attr(
    //       'transform',
    //       d =>
    //         `translate(${x(d.data.year) -
    //           (barWidth - x.bandwidth()) / 2 -
    //           15}, ${y(d[1]) + 10})`
    //     );

    //   partyLabelsGroups
    //     .append('text')
    //     .text(d => {
    //       if (d.key != 'didnotvote') {
    //         return `${parseFloat(
    //           ((d.data[d.key] / d.data['registered_voters']) * 100).toFixed(1)
    //         )}%`;
    //       }
    //     })
    //     .style('font-weight', 'bold')
    //     .attr('class', d => `${d.key}-text label`);

    //   partyLabelsGroups
    //     .append('circle')
    //     .attr('r', 10)
    //     .attr('cy', -4.5)
    //     .attr('cx', -15)
    //     .attr('class', d => `${d.key}-circle`);

    //   partyLabelsGroups
    //     .append('text')
    //     .text(d => d.key[0])
    //     .attr('dy', 0)
    //     .attr('dx', -19)
    //     .attr('class', 'circle-text');
    // }
  }

  render() {
    const { yearData, toggle } = this.props;
    return (
      <div>
        <div class="bar-chart-header">
          <p class="election-type">{yearData.label} primary</p>
          <h1>
            {yearData.year} turnout{' '}
            {toggle.is_there_sos_data == 'YES' &&
            yearData.year == '2022' &&
            toggle.is_election_over == 'NO' ? (
              <span class="as-of">after early voting</span> // as of {toggle.day}
            ) : (
              <span></span>
            )}
          </h1>
          <p>
            {yearData.year != '2022' || toggle.is_there_sos_data == 'YES'
              ? `${parseFloat(
                  (
                    parseFloat(
                      (
                        (yearData.republican / yearData.registered_voters) *
                        100
                      ).toFixed(1)
                    ) +
                    parseFloat(
                      (
                        (yearData.democratic / yearData.registered_voters) *
                        100
                      ).toFixed(1)
                    )
                  ).toFixed(1)
                )}% of `
              : ''}
            {intcomma(intword(yearData.registered_voters))}
          </p>
          <p>registered voters</p>
        </div>
        <div class={`bar-chart-container-${yearData.year} bar-chart`}></div>
      </div>
    );
  }
}

export default BarChart;
